// src/components/mission/MissionDiary.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Pagination,
  Grid,
} from '@mui/material';
import MissionCard from './MissionCard'; // Assuming MissionCard.jsx is in the same folder or './MissionCard'
import { useTranslation } from "react-i18next";
import './styles.css';
import {useGetMissionQuery, useClaimMissionMutation, useClaimDailyLoginMissionMutation } from "../../features/api/missionApiSlice";
import Swal from "sweetalert2";

// Define the MissionResponse interface (for better readability/type safety)
// In a real TS project, this would be an interface. Here, it's just a comment.
/**
 * @typedef {Object} MissionResponse
 * @property {string} missionName
 * @property {string} missionType // "daily" or "accumulative"
 * @property {string} missionMode // Not explicitly used in UI, but good to have
 * @property {number} target
 * @property {number} amount
 * @property {string} rewardType // "points", "fixed", "random"
 * @property {number} progress // 0 to 1
 * @property {boolean} completed
 * @property {boolean} claimed
 * @property {string} desc
 * @property {string} rewardDesc // e.g., "100 point(s)", "8.00 PHP", "Up to 18.00 PHP"
 */


const mockMissionsData = [
  // Daily Missions
  {
    missionName: 'Daily Login',
    missionType: 'daily',
    missionMode: 'once',
    target: 0, amount: 0, progress: 0, // No progress for daily login
    rewardType: 'RANDOM_REWARD',
    completed: false, claimed: false, // Not claimed yet
    desc: 'Target 00:00 ~ 23:59',
    rewardDesc: 'RANDOM_REWARD Reward Up to 18.00 PHP',
    hasDeposit: false, hasTurnover: false, // No specific deposit/turnover action
  },
  {
    missionName: 'Daily Login',
    missionType: 'daily',
    missionMode: 'once',
    target: 0, amount: 0, progress: 0,
    rewardType: 'RANDOM_REWARD',
    completed: true, claimed: true, // Already claimed
    desc: 'Target 00:00 ~ 23:59',
    rewardDesc: 'RANDOM_REWARD Reward Up to 18.00 PHP',
    hasDeposit: false, hasTurnover: false,
  },
  {
    missionName: 'Daily Deposit(Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 300, amount: 0, progress: 0,
    rewardType: 'points',
    completed: false, claimed: false,
    desc: 'Target 300 PHP or more',
    rewardDesc: '100 point(s)',
    hasDeposit: true, hasTurnover: false, // This mission requires a deposit
  },
  {
    missionName: 'Daily Deposit(Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 1000, amount: 300, progress: 0.3,
    rewardType: 'points',
    completed: false, claimed: false,
    desc: 'Target 1000 PHP or more',
    rewardDesc: '100 point(s)',
    hasDeposit: true, hasTurnover: false,
  },
  {
    missionName: 'Daily Deposit(Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 2000, amount: 2000, progress: 1,
    rewardType: 'points',
    completed: true, claimed: false, // Ready to claim
    desc: 'Target 2000 PHP or more',
    rewardDesc: '100 point(s)',
    hasDeposit: true, hasTurnover: false,
  },
  {
    missionName: 'Daily Deposit(Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 500, amount: 500, progress: 1,
    rewardType: 'FIXED_REWARD',
    completed: true, claimed: true, // Completed and claimed
    desc: 'Target 500 PHP or more',
    rewardDesc: '8.00 PHP',
    hasDeposit: true, hasTurnover: false,
  },
  {
    missionName: 'Daily Deposit(Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 3000, amount: 1500, progress: 0.5,
    rewardType: 'FIXED_REWARD',
    completed: false, claimed: false,
    desc: 'Target 3000 PHP or more',
    rewardDesc: '38.00 PHP',
    hasDeposit: true, hasTurnover: false,
  },
  {
    missionName: 'Daily Turnover (Accumulated)',
    missionType: 'daily',
    missionMode: 'accumulated',
    target: 5000, amount: 2500, progress: 0.5,
    rewardType: 'FIXED_REWARD',
    completed: false, claimed: false,
    desc: 'Target 5000 PHP Turnover or more',
    rewardDesc: '50.00 PHP',
    hasDeposit: false, hasTurnover: true, // This mission requires playing
  },
  {
    missionName: 'Daily Share',
    missionType: 'daily',
    missionMode: 'once',
    target: 0, amount: 0, progress: 0,
    rewardType: 'FIXED_REWARD',
    completed: true, claimed: false, // Ready to claim
    desc: 'Share on social media',
    rewardDesc: '5.00 PHP',
    hasDeposit: false, hasTurnover: false,
  },

  // Accumulative Missions
  {
    missionName: 'Accumulative Deposit (Weekly)',
    missionType: 'accumulative',
    missionMode: 'accumulated',
    target: 10000, amount: 3000, progress: 0.3,
    rewardType: 'points',
    completed: false, claimed: false,
    desc: 'Target 10000 PHP this week',
    rewardDesc: '500 point(s)',
    hasDeposit: true, hasTurnover: false,
  },
  {
    missionName: 'Accumulative Turnover (Monthly)',
    missionType: 'accumulative',
    missionMode: 'accumulated',
    target: 50000, amount: 50000, progress: 1,
    rewardType: 'FIXED_REWARD',
    completed: true, claimed: false, // Ready to claim
    desc: 'Target 50000 PHP this month',
    rewardDesc: '200.00 PHP',
    hasDeposit: false, hasTurnover: true,
  },
  {
    missionName: 'Invite Friends (Total)',
    missionType: 'accumulative',
    missionMode: 'total',
    target: 5, amount: 2, progress: 0.4,
    rewardType: 'FIXED_REWARD',
    completed: false, claimed: false,
    desc: 'Invite 5 friends to register',
    rewardDesc: '100.00 PHP',
    hasDeposit: false, hasTurnover: false,
  },
  // --- Combined Mission Example: ACCUMULATED_DEPOSIT_WITH_TURNOVER ---
  {
    missionName: 'ACCUMULATED_DEPOSIT_WITH_TURNOVER',
    missionType: 'accumulative',
    missionMode: 'accumulated',
    desc: 'Deposit 2000 PHP & Turnover 10000 PHP for bonus',
    rewardType: 'FIXED_REWARD',
    rewardDesc: '100.00 PHP Bonus',
    completed: false, // Overall mission not completed
    claimed: false,   // Overall mission not claimed

    // Deposit part specific fields
    depositTarget: 2000,
    depositAmount: 1000,
    depositProgress: 0.5,
    depositCompleted: false,
    depositClaimed: false,
    hasDeposit: true,

    // Turnover part specific fields
    turnoverTarget: 10000,
    turnoverAmount: 3000,
    turnoverProgress: 0.3,
    turnoverCompleted: false,
    turnoverClaimed: false,
    hasTurnover: true,
  },
  {
    missionName: 'ACCUMULATED_DEPOSIT_WITH_TURNOVER',
    missionType: 'accumulative',
    missionMode: 'accumulated',
    desc: 'Deposit 500 PHP & Turnover 2000 PHP for bonus (ready to claim parts)',
    rewardType: 'FIXED_REWARD',
    rewardDesc: '20.00 PHP Bonus',
    completed: true, // Overall mission completed, but not claimed
    claimed: false,

    // Deposit part
    depositTarget: 500,
    depositAmount: 500,
    depositProgress: 1,
    depositCompleted: true,
    depositClaimed: false, // Deposit part is completed, ready to claim
    hasDeposit: true,

    // Turnover part
    turnoverTarget: 2000,
    turnoverAmount: 2000,
    turnoverProgress: 1,
    turnoverCompleted: true,
    turnoverClaimed: false, // Turnover part is completed, ready to claim
    hasTurnover: true,
  },
  {
    missionName: 'ACCUMULATED_DEPOSIT_WITH_TURNOVER',
    missionType: 'accumulative',
    missionMode: 'accumulated',
    desc: 'Deposit 100 PHP & Turnover 500 PHP for bonus (fully claimed)',
    rewardType: 'FIXED_REWARD',
    rewardDesc: '5.00 PHP Bonus',
    completed: true,
    claimed: true, // Overall mission claimed (implies both parts were claimed)

    // Deposit part
    depositTarget: 100,
    depositAmount: 100,
    depositProgress: 1,
    depositCompleted: true,
    depositClaimed: true, // Deposit part claimed
    hasDeposit: true,

    // Turnover part
    turnoverTarget: 500,
    turnoverAmount: 500,
    turnoverProgress: 1,
    turnoverCompleted: true,
    turnoverClaimed: true, // Turnover part claimed
    hasTurnover: true,
  },
];

const itemsPerPage = 5; // Adjust as needed for pagination

const MissionDiary = ({userId}) => {
  const [activeTab, setActiveTab] = useState('DAILY');
  const [activeCalendar, setActiveCalendar] = useState('daily');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const { t } = useTranslation();

  const {
    data: missionData,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetMissionQuery({ userId }, { skip: !userId });
  const [claimMission, { isClaimMissionLoading }] = useClaimMissionMutation();
  const [claimDailyLoginMission, { isClaimDailyLoginMissionLoading }] = useClaimDailyLoginMissionMutation();

  useEffect(() => {
    console.log("userId",userId);
    if (missionData?.result) {
      const missionsForTab = missionData.result.filter(
        (m) => m.missionMode === activeTab
      );
      setFilteredMissions(missionsForTab);
      setCurrentPage(1);
    } else {
      setFilteredMissions([]);
    }
  }, [missionData, activeTab]);
  // useEffect(() => {
  //   // Filter missions based on the active tab
  //   const missionsForTab = mockMissionsData.filter(
  //     (mission) => mission.missionType === activeTab
  //   );
  //   setFilteredMissions(missionsForTab);
  //   setCurrentPage(1); // Reset page when tab changes
  // }, [activeTab]);

  useEffect(() => {
    
  }, [activeCalendar]);

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  // const handleMissionAction = async (mission) => {
  //   const confirmResult = await Swal.fire({
  //     title: `Claim "${mission.missionName}"?`,
  //     text: "Are you sure you want to claim this mission reward?",
  //     icon: "question",
  //     showCancelButton: true,
  //     confirmButtonText: "Yes, claim it!",
  //     cancelButtonText: "No, cancel",
  //   });

  //   if (!confirmResult.isConfirmed) return;

  //   try {
  //     const result = await claimMission({
  //       playerMissionStatusId: mission.playerMissionStatusId,
  //     }).unwrap();

  //     console.log("Claim result:", result);

  //     await Swal.fire({
  //       title: "Mission Claimed!",
  //       text: `"${mission.missionName}" reward has been claimed successfully.`,
  //       icon: "success",
  //     });
  //   } catch (err) {
  //     console.error("Failed to claim mission:", err);

  //     await Swal.fire({
  //       title: "Error",
  //       text: `Failed to claim "${mission.missionName}". Please try again.`,
  //       icon: "error",
  //     });
  //   }
  // };

  const handleMissionAction = async (mission) => {
    try {
      let response = null;
      if (mission.missionType === "DAILY_LOGIN" || mission.missionType === "DAILY_FREE_REWARD"){
        response = await claimDailyLoginMission({
          userId: userId,
          missionBonusId: mission.missionBonusId,
        }).unwrap();
      }
      else {
        response = await claimMission({
          playerMissionStatusId: mission.playerMissionStatusId,
        }).unwrap();
      }
      console.log("Claim result:", response);
  
      await Swal.fire({
        title: "Claim successful!",
        text: `You have won ${response.result}!`,
        icon: "success",
      });
    } catch (err) {
      console.error("Failed to claim mission:", err);
  
      await Swal.fire({
        title: "Error",
        text: `Failed to claim "${mission.missionName}". Please try again.`,
        icon: "error",
      });
    }
  };

  // Calculate missions for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const missionsToDisplay = filteredMissions.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredMissions.length / itemsPerPage);

  return (
    <Container maxWidth="md" sx={{ py: 0 }}>
      {/* <Typography className="typography-global" variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        MISSION DIARY
      </Typography> */}
      <Typography className="typography-global" fontWeight="bold" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                    {t("mission.missiondiary")}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          aria-label="mission tabs"
          centered
          className="custom-tabs-root"
        >
          <Tab label={t('mission.dailymission')} value="DAILY" sx={{ textTransform: 'none' }}  />
          <Tab label={t('mission.accumulativemission')} value="ACCUMULATED" sx={{ textTransform: 'none' }}  />
        </Tabs>
      </Box>

      <Grid container spacing={2}>
        {missionsToDisplay.length > 0 ? (
          missionsToDisplay.map((mission, index) => (
            <Grid item xs={12} key={index}>
              <MissionCard mission={mission} onAction={handleMissionAction} />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography className="typography-global" variant="body1" color={process.env.REACT_APP_COLOR_TEXT} align="center">
              {t('mission.nomissionfound')}
            </Typography>
          </Grid>
        )}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
            className="custom-pagination"
          />
        </Box>
      )}
    </Container>
  );
};

export default MissionDiary;